import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { tick, toggleClock, stopClock, nextRound, startBreak, resetClock } from '@/store/clockSlice';
import { setCurrentRound, addHistoryEntry } from '@/store/tournamentSlice';
import { formatTime } from '@/utils/timeUtils';
import clsx from 'clsx';
import { Play, Pause, RotateCcw, SkipForward } from 'lucide-react';

interface TournamentClockProps {
  className?: string;
  showControls?: boolean;
}

export const TournamentClock: React.FC<TournamentClockProps> = ({ 
  className,
  showControls = true 
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isRunning, secondsRemaining, currentRound, isBreak } = useSelector(
    (state: RootState) => state.clock
  );
  const tournament = useSelector((state: RootState) => state.tournament.current);

  // Clock tick effect
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(tick());
    }, 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Auto-advance to next round when time expires
  useEffect(() => {
    if (secondsRemaining === 0 && tournament) {
      const currentRoundData = tournament.rounds[currentRound - 1];
      
      if (isBreak) {
        // End break, go to next round
        dispatch(nextRound());
        dispatch(addHistoryEntry(`End break after round ${currentRound - 1}`));
        
        if (currentRound <= tournament.rounds.length) {
          const nextRoundData = tournament.rounds[currentRound - 1];
          dispatch(resetClock(nextRoundData.minutes * 60));
        }
      } else if (currentRoundData && currentRoundData.breakMinutes > 0) {
        // Start break after this round
        dispatch(startBreak());
        dispatch(resetClock(currentRoundData.breakMinutes * 60));
        dispatch(addHistoryEntry(`End round ${currentRound}, starting ${currentRoundData.breakMinutes} minute break`));
      } else if (currentRound < tournament.rounds.length) {
        // No break, go directly to next round
        dispatch(nextRound());
        dispatch(addHistoryEntry(`End round ${currentRound}`));
        
        const nextRoundData = tournament.rounds[currentRound];
        if (nextRoundData) {
          dispatch(resetClock(nextRoundData.minutes * 60));
        }
      } else {
        // Tournament complete
        dispatch(stopClock());
        dispatch(addHistoryEntry('Tournament completed'));
      }
    }
  }, [secondsRemaining, currentRound, isBreak, tournament, dispatch]);

  const handleToggleClock = useCallback(() => {
    dispatch(toggleClock());
    
    if (!isRunning) {
      dispatch(addHistoryEntry('Clock started'));
    } else {
      dispatch(addHistoryEntry('Clock paused'));
    }
  }, [dispatch, isRunning]);

  const handleResetClock = useCallback(() => {
    if (tournament && tournament.rounds[currentRound - 1]) {
      const roundData = tournament.rounds[currentRound - 1];
      const minutes = isBreak ? roundData.breakMinutes : roundData.minutes;
      dispatch(resetClock(minutes * 60));
      dispatch(addHistoryEntry(`Clock reset to ${minutes} minutes`));
    }
  }, [tournament, currentRound, isBreak, dispatch]);

  const getCurrentRoundData = () => {
    if (!tournament || !tournament.rounds[currentRound - 1]) {
      return null;
    }
    return tournament.rounds[currentRound - 1];
  };

  const roundData = getCurrentRoundData();
  const isPaused = !isRunning;

  const timeDisplay = formatTime(secondsRemaining);
  const isWarning = secondsRemaining <= 60 && secondsRemaining > 0;
  const isCritical = secondsRemaining <= 30 && secondsRemaining > 0;

  return (
    <div className={clsx(
      'bg-gradient-to-br from-poker-green to-poker-felt',
      'border-4 border-poker-gold rounded-lg shadow-2xl',
      'text-white font-display',
      className
    )}>
      {/* Header */}
      <div className="bg-poker-gold text-black px-6 py-3 rounded-t-md">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">
            {isBreak ? 'BREAK' : `ROUND ${currentRound}`}
          </h2>
          {tournament && (
            <span className="text-sm font-medium">
              {tournament.settings.name}
            </span>
          )}
        </div>
      </div>

      {/* Main Clock Display */}
      <div className="p-8 text-center">
        <div className={clsx(
          'text-8xl font-mono font-bold mb-4 transition-all duration-300',
          {
            'text-red-400 animate-pulse': isCritical && isRunning,
            'text-yellow-400': isWarning && !isCritical && isRunning,
            'text-white': (!isWarning && isRunning) || isPaused,
            'opacity-60': isPaused,
          }
        )}>
          {timeDisplay}
        </div>

        {isPaused && (
          <div className="text-4xl font-bold text-red-400 animate-pulse mb-4">
            PAUSED
          </div>
        )}

        {/* Blinds Display */}
        {roundData && !isBreak && (
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="bg-black/30 rounded-lg p-4">
              <div className="text-sm text-gray-300 mb-1">Small Blind</div>
              <div className="text-2xl font-bold text-poker-gold">
                {tournament?.settings.currencySymbol}{roundData.smallBlind.toLocaleString()}
              </div>
            </div>
            <div className="bg-black/30 rounded-lg p-4">
              <div className="text-sm text-gray-300 mb-1">Big Blind</div>
              <div className="text-2xl font-bold text-poker-gold">
                {tournament?.settings.currencySymbol}{roundData.bigBlind.toLocaleString()}
              </div>
            </div>
          </div>
        )}

        {/* Ante Display */}
        {roundData && roundData.ante > 0 && !isBreak && (
          <div className="bg-black/30 rounded-lg p-3 mb-6">
            <div className="text-sm text-gray-300 mb-1">Ante</div>
            <div className="text-xl font-bold text-poker-gold">
              {tournament?.settings.currencySymbol}{roundData.ante.toLocaleString()}
            </div>
          </div>
        )}

        {/* Controls */}
        {showControls && (
          <div className="flex justify-center gap-4">
            <button
              onClick={handleToggleClock}
              className={clsx(
                'flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all',
                'hover:scale-105 active:scale-95',
                {
                  'bg-green-600 hover:bg-green-700 text-white': !isRunning,
                  'bg-red-600 hover:bg-red-700 text-white': isRunning,
                }
              )}
            >
              {isRunning ? (
                <>
                  <Pause size={20} />
                  Pause
                </>
              ) : (
                <>
                  <Play size={20} />
                  Start
                </>
              )}
            </button>

            <button
              onClick={handleResetClock}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all hover:scale-105 active:scale-95"
            >
              <RotateCcw size={20} />
              Reset
            </button>

            {currentRound < (tournament?.rounds.length || 0) && (
              <button
                onClick={() => {
                  dispatch(setCurrentRound(currentRound + 1));
                  dispatch(addHistoryEntry(`Manually advanced to round ${currentRound + 1}`));
                }}
                className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-all hover:scale-105 active:scale-95"
              >
                <SkipForward size={20} />
                Next Round
              </button>
            )}
          </div>
        )}
      </div>

      {/* Round Progress */}
      {tournament && (
        <div className="px-6 pb-4">
          <div className="bg-black/30 rounded-lg p-3">
            <div className="flex justify-between text-sm text-gray-300 mb-2">
              <span>Round Progress</span>
              <span>{currentRound} / {tournament.rounds.length}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div
                className="bg-poker-gold h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${Math.min(100, (currentRound / tournament.rounds.length) * 100)}%`
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};